/**
 * src/pages/AssistantReports.tsx
 * 共通コンポーネントを使用するアシスタント用日報ページ
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../components/AuthGuard'
import { useIsMobile } from '../hooks/useIsMobile'
import { ReportDetailModal } from '../components/ReportDetailModal'
import { ReportFormModal } from '../components/ReportFormModal'
import { REMINDER_STORAGE_KEY, showOsNotification } from '../hooks/useReportReminder'
import { TempEmailSpotlight } from '../components/TempEmailBanner'
import { useAssistantMonth } from '../hooks/useAssistantMonth'
import { thisMonthJst } from '../utils/jst'
import {
  STAGE_LABELS,
  KAIKAI_KIKI_LABEL,
  isKaikaiKiki,
  parseWorkDetails,
  parseImageUrls,
  totalPages,
} from '../components/reportUtils'

// ================================================================
// 日報リマインダー通知設定 UI
// ================================================================
function ReminderSettings() {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    'Notification' in window ? Notification.permission : 'denied'
  )
  const [enabled, setEnabled] = useState(() =>
    localStorage.getItem(REMINDER_STORAGE_KEY) === 'enabled'
  )
  const [testSending, setTestSending] = useState(false)
  const [testSent, setTestSent] = useState(false)

  // Notification API が存在しないブラウザ（iOS SafariなどPWA以外）は非表示
  if (!('Notification' in window)) return null

  const handleEnable = async () => {
    const result = await Notification.requestPermission()
    setPermission(result)
    if (result === 'granted') {
      localStorage.setItem(REMINDER_STORAGE_KEY, 'enabled')
      setEnabled(true)
    }
  }

  const handleDisable = () => {
    localStorage.removeItem(REMINDER_STORAGE_KEY)
    setEnabled(false)
  }

  const handleTestNotification = async () => {
    setTestSending(true)
    setTestSent(false)
    try {
      await showOsNotification({
        title: 'MangaOS 通知テスト',
        body: 'この通知が表示されれば、日報リマインダーを受け取れます。',
        tag: `mangaos-test-${Date.now()}`,
        renotify: false,
        requireInteraction: false,
      })
      setTestSent(true)
      setTimeout(() => setTestSent(false), 4000)
    } finally {
      setTestSending(false)
    }
  }

  // 通知がブロックされている場合
  if (permission === 'denied') {
    return (
      <div style={{
        background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
        borderRadius: 8, padding: '12px 14px', fontSize: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 15 }}>🔕</span>
          <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>
            ブラウザ通知がブロックされています
          </span>
        </div>
        <div style={{ color: 'var(--ink-2)', lineHeight: 1.6, paddingLeft: 23 }}>
          <div>ブラウザまたはWindows/Macの通知設定でMangaOSの通知を許可してください。</div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: 'var(--ink-3)' }}>
            <li>ブラウザのアドレスバー横の鍵アイコン →「通知」→「許可」に変更</li>
            <li>Windowsの場合は「設定 → システム → 通知」でブラウザの通知もオンに</li>
            <li>集中モード・おやすみモードがオンの場合も通知が届きません</li>
          </ul>
        </div>
      </div>
    )
  }

  // 有効化済み
  if (enabled && permission === 'granted') {
    return (
      <div style={{
        background: 'var(--green-light)', border: '1px solid rgba(45,122,58,0.2)',
        borderRadius: 8, padding: '12px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 15 }}>🔔</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>
            日報リマインダー 有効
          </span>
          <span style={{
            fontSize: 10, color: 'var(--green)', background: 'rgba(45,122,58,0.12)',
            borderRadius: 4, padding: '1px 6px',
          }}>
            通知許可: 済
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 10, paddingLeft: 23 }}>
          <div>毎日 19:55 頃にOS通知（Windowsの通知欄など）でお知らせします。</div>
          <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
            この端末・このブラウザでのみ有効です。PCがスリープ中・ブラウザが終了している場合は通知されません。
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, paddingLeft: 23 }}>
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: 11, whiteSpace: 'nowrap' }}
            onClick={handleTestNotification}
            disabled={testSending}
          >
            {testSending ? '送信中...' : testSent ? '通知を送りました' : 'テスト通知を送る'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}
            onClick={handleDisable}
          >
            無効にする
          </button>
        </div>
      </div>
    )
  }

  // 未設定 または 通知許可済みだがリマインダーが無効
  return (
    <div style={{
      background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
      borderRadius: 8, padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }}>🔔</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-1)' }}>
          日報リマインダー
        </span>
        <span style={{
          fontSize: 10, color: 'var(--ink-3)', background: 'var(--paper-3)',
          borderRadius: 4, padding: '1px 6px',
        }}>
          {permission === 'granted' ? '通知許可: 済・リマインダー: OFF' : '未設定'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 10, paddingLeft: 23 }}>
        <div>
          日報リマインダーを有効にすると、MangaOSを開いている間、19:55 頃にOS通知でお知らせします。
        </div>
        <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
          この端末・このブラウザでのみ有効です。PCがスリープ中・ブラウザが終了している場合は通知されません。
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, paddingLeft: 23 }}>
        <button
          className="btn btn-outline btn-sm"
          style={{ whiteSpace: 'nowrap', fontSize: 12 }}
          onClick={handleEnable}
        >
          {permission === 'granted' ? 'リマインダーを有効にする' : '通知を許可して有効にする'}
        </button>
        {permission === 'granted' && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}
            onClick={handleTestNotification}
            disabled={testSending}
          >
            {testSending ? '送信中...' : testSent ? '通知を送りました' : 'テスト通知を送る'}
          </button>
        )}
      </div>
    </div>
  )
}

// ================================================================
// モバイル用 日報カード
// ================================================================
function ReportCard({ r, myAsstId, onClick }: { r: any; myAsstId: string; onClick: () => void }) {
  const workDetails = parseWorkDetails(r.work_details)
  const imgCount    = parseImageUrls(r.image_urls).length
  const isMyReport  = r.assistant_id === myAsstId
  const pages       = workDetails ? totalPages(workDetails) : (r.pages_done ?? 0)

  const summaryItems = workDetails
    ? workDetails.slice(0, 2).map((d) => {
        const kk = isKaikaiKiki(d.work_id, d.work_title)
        return kk
          ? `${KAIKAI_KIKI_LABEL}${d.memo ? '：' + d.memo.slice(0, 20) : ''}`
          : [d.work_title, d.episode_number ? `#${d.episode_number}` : '',
              STAGE_LABELS[d.stage ?? ''] ?? d.stage, d.pages_done ? `${d.pages_done}P` : '']
              .filter(Boolean).join(' ')
      })
    : [[r.work_title, STAGE_LABELS[r.stage ?? ''] ?? r.stage, r.pages_done ? `${r.pages_done}P` : '']
        .filter(Boolean).join(' ')].filter(Boolean)

  return (
    <div onClick={onClick} style={{
      background: isMyReport ? 'rgba(45,122,58,0.04)' : 'var(--paper)',
      border: `1px solid ${isMyReport ? 'rgba(45,122,58,0.2)' : 'var(--paper-3)'}`,
      borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600 }}>{r.report_date}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: isMyReport ? 700 : 500 }}>{r.assistant_name}</span>
          {isMyReport && <span style={{ fontSize: 10, color: 'var(--green)', background: '#e8f4e9', borderRadius: 4, padding: '1px 5px' }}>自分</span>}
          {r.is_help && <span className="badge badge-blue" style={{ fontSize: 10 }}>ヘルプ</span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {pages > 0 && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
            color: 'var(--green)', background: 'var(--green-light)', borderRadius: 4, padding: '1px 7px',
          }}>
            合計{pages}P
          </span>
        )}
        {workDetails && workDetails.length > 1 && (
          <span style={{ fontSize: 11, color: 'var(--blue)' }}>{workDetails.length}作業</span>
        )}
        {imgCount > 0 && <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 'auto' }}>📷 {imgCount}枚</span>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {summaryItems.filter(Boolean).map((s, i) => (
          <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>・{s}</div>
        ))}
        {workDetails && workDetails.length > 2 && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>…他{workDetails.length - 2}件</div>
        )}
      </div>

      {r.handover_notes && r.handover_notes !== '特になし' && (
        <div style={{ fontSize: 11, color: 'var(--amber)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📋 {r.handover_notes}
        </div>
      )}
    </div>
  )
}


function monthBounds(ym: string): { startDate: string; endDate: string } {
  const [year, month] = ym.split('-').map(Number)
  const lastDay = new Date(year, month, 0).getDate()

  return {
    startDate: `${ym}-01`,
    endDate: `${ym}-${String(lastDay).padStart(2, '0')}`,
  }
}

function shiftMonth(ym: string, delta: number): string {
  const [year, month] = ym.split('-').map(Number)
  const d = new Date(year, month - 1 + delta, 1)

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(ym: string): string {
  const [year, month] = ym.split('-')
  return `${year}年${Number(month)}月`
}
// ================================================================
// メインページ
// ================================================================
export default function AssistantReports() {
  const { user }   = useAuth()
  const isMobile   = useIsMobile()
  const [month, setMonth]      = useAssistantMonth(user?.assistant_id)
  const [reports,     setReports]     = useState<any[]>([])
  const [works,       setWorks]       = useState<{ id: string; title: string }[]>([])
  const [loading,     setLoading]     = useState(true)
  const [selected,    setSelected]    = useState<any | null>(null)
  const [showForm,    setShowForm]    = useState(false)
  const [editReport,  setEditReport]  = useState<any | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { startDate, endDate } = monthBounds(month)

      const [rpts, ws] = await Promise.all([
        api.reports.list({
          start_date: startDate,
          end_date: endDate,
        }),
        api.works.list().catch(() => []),
      ])

      setReports(rpts)
      setWorks(ws)
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => { load() }, [load])

  const assistantId = user?.assistant_id ?? ''

  return (
    <div>
      {/* 仮メール登録案内（3回ログインごと） */}
      <TempEmailSpotlight />

      {/* 日報リマインダー通知設定（アシスタントのみ） */}
      <div style={{ marginBottom: 12 }}>
        <ReminderSettings />
      </div>

      {/* 日報フォームモーダル（新規） */}
      {showForm && assistantId && (
        <ReportFormModal
          mode="assistant"
          myAssistantId={assistantId}
          works={works}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load() }}
        />
      )}

      {/* 日報フォームモーダル（編集） */}
      {editReport && assistantId && (
        <ReportFormModal
          mode="assistant"
          myAssistantId={assistantId}
          works={works}
          initialReport={editReport}
          onClose={() => setEditReport(null)}
          onSaved={() => { setEditReport(null); load() }}
        />
      )}

      {/* 操作バー：月ナビ + 主操作ボタン */}
      <div className="card-header" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 0 }}>
        {/* 左：タイトル + 件数 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="card-title">日報一覧</span>
          <span className="badge badge-gray">{monthLabel(month)}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{reports.length}件</span>
        </div>

        {/* 右：月ナビ + 日報を提出 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'flex-start' : 'flex-end',
        }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            前月
          </button>

          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ fontSize: 12, width: 134 }}
            aria-label="表示月"
          />

          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            翌月
          </button>

          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setMonth(thisMonthJst())}
          >
            今月
          </button>

          {assistantId && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowForm(true)}
              style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 40 : undefined }}
            >
              + 日報を提出
            </button>
          )}
        </div>
      </div>

        {loading ? (
          <div className="loading" style={{ padding: 32 }}>LOADING...</div>
        ) : reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)' }}>
            {monthLabel(month)}の日報はありません
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
            {reports.map((r) => (
              <ReportCard key={r.id} r={r} myAsstId={assistantId} onClick={() => setSelected(r)} />
            ))}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>日付</th><th>担当者</th><th>作業</th><th>合計P</th><th>引継ぎ</th><th>画像</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const wds      = parseWorkDetails(r.work_details)
                  const pages    = wds ? totalPages(wds) : (r.pages_done ?? 0)
                  const imgCount = parseImageUrls(r.image_urls).length
                  const isMe     = r.assistant_id === assistantId
                  return (
                    <tr key={r.id} onClick={() => setSelected(r)}
                      style={{ cursor: 'pointer', background: isMe ? 'rgba(45,122,58,0.04)' : undefined }}>
                      <td className="mono">{r.report_date}</td>
                      <td style={{ fontWeight: isMe ? 700 : 500 }}>
                        {r.assistant_name}
                        {isMe && <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 4 }}>自分</span>}
                        {r.is_help && <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 4 }}>ヘルプ</span>}
                      </td>
                      <td style={{ fontSize: 12, maxWidth: 240 }}>
                        {wds
                          ? wds.slice(0, 2).map((d, i) => {
                              const kk = isKaikaiKiki(d.work_id, d.work_title)
                              return (
                                <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {kk ? KAIKAI_KIKI_LABEL
                                      : [d.work_title, STAGE_LABELS[d.stage ?? ''] ?? d.stage, d.pages_done ? `${d.pages_done}P` : ''].filter(Boolean).join(' ')}
                                </div>
                              )
                            })
                          : <span style={{ color: 'var(--ink-3)' }}>
                              {[r.work_title, STAGE_LABELS[r.stage ?? ''] ?? r.stage].filter(Boolean).join(' ') || '—'}
                            </span>
                        }
                        {wds && wds.length > 2 && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>…他{wds.length - 2}件</span>}
                      </td>
                      <td className="mono" style={{ fontWeight: 600 }}>{pages > 0 ? `${pages}P` : '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 160,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.handover_notes
                          ? (r.handover_notes === '特になし' ? <span style={{ opacity: 0.4 }}>特になし</span> : r.handover_notes)
                          : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: imgCount > 0 ? 'var(--blue)' : 'var(--ink-3)' }}>
                        {imgCount > 0 ? `📷 ${imgCount}枚` : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      

      {/* 詳細モーダル */}
      {selected && (
        <ReportDetailModal
          report={selected}
          onClose={() => setSelected(null)}
          isMobile={isMobile}
          myAssistantId={assistantId}
          onDeleted={() => { setSelected(null); load() }}
          onEdit={(r) => { setSelected(null); setEditReport(r) }}
        />
      )}
    </div>
  )
}